// angular
import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import * as _ from 'lodash';

// services
import * as moment from 'moment';
import { BooksService } from '../../../core/services/data/books.service';
import { FeaturedBooksService } from '../../../core/services/data/featured-books.service';
import { InputControlService } from '../../../core/services/input-control.service';
import { ModalService } from '../../../core/services/modal.service';
import { SiteAdvertsService } from '../../../core/services/data/site-adverts.service';
import { SitesService } from '../../../core/services/data/sites.service';
import { UserService } from '../../../core/services/data/user.service';
import { PurchaseProcessService } from '../../../core/services/data/purchase-process.service';
import { ExperimentsService } from '../../../core/services/experiments.service';

// classes
import { Book } from '../../../classes/book';
import { FeaturedBook } from '../../../classes/featured-book';
import { FeaturesStep4Form } from './features-step4-form';
import { FeaturesStep4FormFreeboosky } from './features-step4-form-freeboosky';
import { FeaturesStep4FormRedFeather } from './features-step4-form-red-feather';
import { FeaturesStep4FormBargainBooksy } from './features-step4-form-bargain-booksy';
import { FeaturesStep4FormNewInBooks } from './features-step4-form-new-in-books';
import { FeaturesStep4FormAudioThicket } from './feature-step4-form-audio-thicket';
import { FeaturesStep4FormFacebookAds } from './features-step4-facebook-ads-form';
import { AnalyticsService } from '../../../core/services/analytics.service';

// interfaces
import { Retailer } from '../../../types/retailer.interface';

import { IBook, ISiteAdvert, ISite, IUser, IWhereAvailable } from '../../../types/datatypes';
import { ScrapeAPIResponse } from '../../../types/responses';
import { IPopulatedFeature } from '../../../types/populated';
import { UtilitiesService } from '../../../core/services/utilities.service';
import { SettingsService } from '../../../core/services/data/settings.service';

// constants
// TODO: Move to book service....
const DEFAULT_BOOK_COVER = '/assets/img/defaults/default_book_image_400w_simple.jpg';
const DEFAULT_AUDIO_BOOK_COVER = '/assets/img/defaults/default_book_image_square_simple.jpg';
// const FACEBOOK_ADS_ID = '5f998d3f3b75580011de6600';

@Component({
	selector: 'app-features-step4',
	templateUrl: './features-step4.component.html',
	styleUrls: ['./features-step4.component.scss'],
})
export class FeaturesStep4Component implements OnInit, OnDestroy {
	public EARLY_ACCESS_THRESHOLD = 14;

	public friendlyPubDate: string;

	@Input() orderEditingId;

	@Input() info;

	@Output() stepChange = new EventEmitter();

	public featuredBook: Partial<IPopulatedFeature>;

	public book: IBook = new Book();

	public formComponents;

	public errorText: string[];

	public errorTitle: string[];

	public amazonError = false;

	public form: UntypedFormGroup;

	public scrapeInProgress = false;

	public uploadingCover = false;

	public bookImageUploadUrl: string;

	public bookImageUploadPath: string;

	// TODO: Figure out if we can name these better, and if they are needed at all.

	public user: IUser;

	public retailers: Retailer[] = [];

	public seriesLinks = [];

	public primaryLink;

	public primaryLinkHover = false;

	public defaultCoverImage = DEFAULT_BOOK_COVER;

	public isFbAdCampaign = false;

	// This is because this code is used for multiple sites with different labels, and isn't structured to be easily dynamic.
	public datePreposition = 'for';

	public purchaseSource;

	public terms = false;

	public invalidPrice = false;

	public linkError = false;

	public bookType = 'amazon';

	public showLinkOptions = false;

	public showBookShelf = true;

	public selectedFromBookShelf = false;

	public showNewValidation = false;

	public linkOptionsAvailable = false;

	public validation = {
		display: false,
		fields: [
			{
				name: 'Amazon Link',
				id: '#amazonLink',
			},
			{
				name: 'Amazon Link',
				id: '#amazonLink',
			},
			{
				name: 'Amazon Link',
				id: '#amazonLink',
			},
		],
	};

	public errorOptions = {
		title: 'There was a problem',
		text: null,
		actions: [{
			type: 'solid',
			text: 'Got it',
			callback: () => {
				this.modalService.close('fs4-error-modal');
			},
		}],
	};

	public inProgress = false;

	public purchaserInfo = {
		firstName: '',
		lastName: '',
		email: '',
	};

	public setBookDetails: { title: string; author: string; isAmz: boolean; link: string };

	private sites$;

	private siteAdvert$;

	private featuredBooks$;

	private seriesIncrement = 0;

	private savedDetails;

	constructor(
		private route: ActivatedRoute,
		private bs: BooksService,
		private fbs: FeaturedBooksService,
		private ics: InputControlService,
		private modalService: ModalService,
		private sas: SiteAdvertsService,
		private ss: SitesService,
		private as: AnalyticsService,
		private us: UserService,
		private purchaseProcessService: PurchaseProcessService,
		private router: Router,
		private utilitiesService: UtilitiesService,
		private settingsService: SettingsService,
		private es: ExperimentsService,
	) {
	}

	get variant() {
		return this.es.getVariant();
	}

	get authorLink() {
		return this.purchaseProcessService.authorLink;
	}

	get pubDate() {
		return this.purchaseProcessService.pubDate;
	}

	get siteSlug(): string {
		return this.purchaseProcessService.siteSlug;
	}

	get site(): ISite {
		return this.purchaseProcessService.site;
	}

	get siteAdvertSlug(): string {
		return this.purchaseProcessService.siteAdvertSlug;
	}

	get siteAdvert(): ISiteAdvert {
		return this.purchaseProcessService.siteAdvert;
	}

	get isGiveaway() {
		return this.siteAdvert?.giveaway?.isGiveaway;
	}

	get featuredBookId() {
		return this.purchaseProcessService.featureId;
	}

	get loading$() {
		return this.purchaseProcessService.loading$;
	}

	_whichForm(site) {
		const { slugs } = this.ss;
		switch (site) {
			case slugs.FREEBOOKSY_SLUG:
				return new FeaturesStep4FormFreeboosky();
			case slugs.RED_FEATHER_SLUG:
				return new FeaturesStep4FormRedFeather();
			case slugs.BARGAIN_BOOKS_SLUG:
				return new FeaturesStep4FormBargainBooksy();
			case slugs.NEW_IN_BOOKS_SLUG:
				return new FeaturesStep4FormNewInBooks();
			case slugs.AUDIO_THICKET_SLUG:
				return new FeaturesStep4FormAudioThicket();
			case slugs.FACEBOOK_ADS_SLUG:
				this.isFbAdCampaign = true;
				return new FeaturesStep4FormFacebookAds();
			default:
				return new FeaturesStep4Form();
		}
	}

	setDatePreposition(siteName) {
		if (this.ss.isFacebookOrAmazonAdSite(siteName)) {
			this.datePreposition = 'starting';
		}
	}

	ngOnInit() {
		this.loading$.subscribe((loading) => {
			if (!loading) {
				this.initialize();
			}
		});

		if (!this.purchaseProcessService.loading) {
			this.initialize();
		}

		this.resetScroll();

		this.settingsService.getNewFeatureValidationSetting().then((showNewValidation) => {
			this.showNewValidation = showNewValidation;
		});

		this.settingsService.getShowLinkOptionsSetting().then((linkOptionsAvailable) => {
			this.linkOptionsAvailable = linkOptionsAvailable;
		});
	}

	initialize() {
		if (!this.site || !this.siteAdvert) return;
		this.friendlyPubDate = moment(this.pubDate, 'YYYYMMDD').format('ddd, MMM DD, YYYY');
		this.featuredBook = new FeaturedBook();
		this.handleSite();
		this.handleSiteAdvert();
		this.handleSiteSlugOneOffs();
		this.adjustFormRequirements();

		this.setDatePreposition(this.siteSlug);
		this.us.fetchUser().then(this.handleUser.bind(this)).catch(() => {});

		if (this.featuredBookId || this.orderEditingId) {
			this.handleFullFeaturedBook();
		}

		const _savedDetails = this.purchaseProcessService.getDetails();

		this.form = InputControlService.toFormGroup(this.formComponents);

		if (_savedDetails) {
			// a featured book exists because the user reran a promotion
			this.savedDetails = _savedDetails;
			this.handleSavedBook();
		} else if (this.purchaseProcessService.feature) {
			// a featured book exists because the user is editing a promotion
			this.savedDetails = { feature: this.purchaseProcessService.feature };
			this.handleSavedBook();
		}
	}

	adjustFormRequirements() {
		if (!this.siteAdvert.allowNonAmazonPrimaryLinks) {
			this.formComponents.link.required = false;
		}
	}

	handleSiteSlugOneOffs() {
		const { slugs } = this.ss;
		if (this.siteSlug === slugs.AUDIO_THICKET_SLUG) {
			this.defaultCoverImage = DEFAULT_AUDIO_BOOK_COVER;
		}

		if (this.siteSlug === slugs.FREEBOOKSY_SLUG) {
			this.formComponents.bookFree.disabled = true;
			this.formComponents.bookFree.value = true;
		}
	}

	handleSite() {
		this.formComponents = this._whichForm(this.siteSlug);
		this.featuredBook._site = this.site;
		this.formComponents.subscribeToList.label = `Add me to the reader list for ${this.site.name} so I'll see my `
        + 'book promo when it runs (Highly Recommended)';

		this.retailers = this.getRetailers();
		this.featuredBook.pubDate = parseInt(this.pubDate, 10);
	}

	handleSiteAdvert() {
		this.featuredBook._book = this.book;

		if (this.isGiveaway) {
			this.formComponents.bookPriceDuringPromo.label = 'Book Price';
		}

		if (this.siteAdvert?.isSeries) {
			this.featuredBook._book.isSeries = true;
		}
		if (this.siteAdvert?.dealOfDay) {
			this.featuredBook.dealOfDay = true;
		}
		this.featuredBook._siteAdvert = this.siteAdvert;
	}

	handleSavedBook() {
		this.featuredBook.seriesLinks = [];
		this.form = InputControlService.toFormGroup(this.formComponents);
		this._patch('description', this.savedDetails.book.description);
		this._patch('bookPriceDuringPromo', this.savedDetails.feature.bookPriceDuringPromo);
		if (this.siteSlug === this.ss.slugs.RED_FEATHER_SLUG) {
			this.handleRedFeatherForm();
		}

		if (this.savedDetails.book.link) {
			this._patch('link', this.savedDetails.book.link);
		}

		this._patch('amazonLink', this.savedDetails.book.amazonLink);
		if (this.savedDetails.feature.ASIN) {
			this.featuredBook.ASIN = this.savedDetails.feature.ASIN;
			this.book.ASIN = this.savedDetails.feature.ASIN;
		}

		if (this.savedDetails.feature.purchaserInfo) {
			const { firstName, lastName, email } = this.savedDetails.feature.purchaserInfo;
			this._patch('firstName', firstName);
			this._patch('lastName', lastName);
			this._patch('email', email);
		}

		if (this.savedDetails.book.whereAvailable) {
			this.setWhereAvailable(this.savedDetails.book.whereAvailable);
		}

		if ((!this.savedDetails.book.title || !this.savedDetails.book.author) && this.savedDetails.book.ASIN) {
			this.scrapeLink();
		} else {
			this._patchList(['title', 'author'], this.savedDetails.book);
			this.featuredBook._book = this.savedDetails.book as IBook;
		}

		if (this.savedDetails.feature.purchaseSource) {
			this.purchaseSource = this.savedDetails.feature.purchaseSource;
		}

		if (this.variant === 'experimental' && Object.keys(this.savedDetails.book).length) {
			this.book = this.savedDetails.book;
		}
	}

	handleFullFeaturedBook() {
		this.setFormValues();

		if (!this.featuredBook.seriesLinks) {
			this.featuredBook.seriesLinks = [];
		}

		this.seriesLinks = this.featuredBook.seriesLinks
			.map((item, index) => ({ id: `series_${index}`, value: item.amazon, price: item.price }));
		this.seriesIncrement = this.seriesLinks.length;

		this.seriesLinks.forEach(
			(item) => {
				(<HTMLInputElement>document.getElementById(item.id)).value = item.value;
			},
		);
		if (this.featuredBookId) {
			this.featuredBook._id = this.featuredBookId;
		}
	}

	setFormValues() {
		Object.keys(this.formComponents).forEach((item) => {
			const value = this.featuredBook._book[item] || this.featuredBook[item];
			this.formComponents.value = value;
		});

		this.setWhereAvailable(this.featuredBook.whereAvailable);

		this.formComponents.email.value = this.featuredBook.purchaserInfo.email.toLowerCase();
		this.formComponents.firstName.value = this.featuredBook.purchaserInfo.firstName;
		this.formComponents.lastName.value = this.featuredBook.purchaserInfo.lastName;
		this.form = InputControlService.toFormGroup(this.formComponents);
	}

	handleRedFeatherForm() {
		const rfrFormValues = [
			'bookLength',
			'nameOfSeries',
			'numberOfBooksInSeries',
			'bookNumber',
			'threesomesOrRomanticScenes',
			'endingOfBook',
			'cheatingOrInfidelity',
			'commonThemes',
		];

		this._patchList(rfrFormValues, this.savedDetails.book);
	}

	/**
	 * Patch form controls using an array of keys
	 * @param keys
	 * @param records
	 * @param includeLinks
	 */

	_patchList(keys: string[], records: Record<string, any>, includeLinks = false) {
		for (const key of keys) {
			this._patch(key, records[key]);
		}
	}

	_patch(formControlName: string, value: string | number | boolean) {
		this.form.controls[formControlName]?.patchValue(value);
	}

	handleUser(user: IUser): void {
		this.user = user;

		if (!this.form || !this.user) return;

		this._patch('email', this.user.username.toLowerCase());
		this._patch('firstName', this.user.firstName);
		this._patch('lastName', this.user.lastName);
	}

	getRetailers() {
		const retailers = this.site.siteDevices.map((item) => {
			let _item = item;
			if (_item === 'Google Audio') _item = 'Google Play Audio';
			const name = _item.toLowerCase().replace(/\s|\./g, '').replace(/audio$/, 'Audio');
			const override = this.site.deviceDisplayOverrides[name];

			return { original: override || _item, name, linkName: `${name}Link`, priceName: `${name}Price` };
		}).filter(({ name }) => !name.includes('kindle'));

		// Move authorstore to first link if it exists and is not already in first position
		const isAuthorStoreIndex = retailers.findIndex(({ name }) => name === 'authorstore');

		if (isAuthorStoreIndex > 0) {
			const [authorStore] = retailers.splice(isAuthorStoreIndex, 1);
			retailers.unshift(authorStore);
		}

		return retailers;
	}

	ngOnDestroy() {
		if (this.featuredBooks$) {
			this.featuredBooks$.unsubscribe();
		}
	}

	noAmazon() {
		this._patch('amazonLink', 'TBD');
	}

	getAmazonError(message) {
		const error = message?.Error?.[0]?.Code?.[0];

		if (error) {
			return error;
		}
		return null;
	}

	bookCoverUpload(data) {
		this.featuredBook._book.amazonImageUrl = data.publicUrl;
		this.bookImageUploadUrl = data.publicUrl;
		this.bookImageUploadPath = data.storagePath;
	}

	bookCoverUploadError(event) {
		// this.errorText = event;
		// this.errorTitle = null;
		this.errorOptions.text = event;
		this.modalService.open('fs4-error-modal');
	}

	isUploadingBookCover(event) {
		this.uploadingCover = !!event;
	}

	async scrapeLink() {
		if (this.scrapeInProgress) return;

		if (!this.form.controls.amazonLink.value) {
			this.showError();
			return;
		}

		this.scrapeInProgress = true;

		const [rawUrl] = this.form.controls.amazonLink.value.split('?');
		const ASIN = Book.parseAmazonId(rawUrl);
		if (ASIN === 'null' || ASIN === null) {
			this.scrapeInProgress = false;
			this._patch('author', 'TBD');
			this._patch('title', 'TBD');
			this.showError();
			return;
		}
		const url = `https://www.amazon.com/dp/${ASIN}`;
		this._patch('amazonLink', url);

		this.bs.scrape(url).subscribe((result: ScrapeAPIResponse) => this.handleScrapeResult(result, { url, ASIN }));
	}

	showError(): void {
		this._patch('amazonLink', 'https://www.amazon.com/dp/null');

		this.errorOptions.title = 'Missing a field!';
		this.errorOptions.text = ['You need to add the Amazon URL field before continuing.'];

		this.modalService.open('fs4-error-modal');
	}

	selectBook(book: IBook): void {
		const _book = { ...book };
		this.book = _book;
		this.selectedFromBookShelf = true;
		this.showBookShelf = false;
		this.featuredBook._book = this.book;
		this.featuredBook.ASIN = this.book.ASIN;

		this._patch('amazonLink', this.book.amazonLink);
		this._patch('author', this.book.author);
		this._patch('title', this.book.title);
	}

	handleScrapeResult({ success, book, scrapeResult, message }: ScrapeAPIResponse, { url, ASIN }) {
		this.scrapeInProgress = false;

		if (!success) {
			const amazonError = this.getAmazonError(message);

			if (amazonError === 'InvalidClientTokenId') {
				// this.errorTitle = ['Amazon Error'];
				this.errorOptions.title = 'Amazon Error';
				this.amazonError = true;
			} else {
				// this.errorText = ['Unable to find your book on Amazon.  Please check the link and try again'];
				this.errorOptions.text = ['Unable to find your book on Amazon.  Please check the link and try again'];
				this.modalService.open('fs4-error-modal');
			}

			return;
		}

		this.form.controls.link.patchValue(url);

		const bookResults = book[ASIN];
		const bookAttributes = bookResults.ItemInfo;
		const bookContributors = bookAttributes.ByLineInfo.Contributors;
		const bookImage = bookResults.Images.Primary.Large.URL || this.bookImageUploadUrl;
		const isAudioBook = !!bookContributors.filter((contributor) => contributor.Role === 'Narrator').length;
		this.book.ASIN = ASIN;
		this.featuredBook.ASIN = ASIN;
		this.book.amazonLink = url;
		this.book.author = bookContributors.filter((contributor) => contributor.Role === 'Author')[0].Name;
		/*
	* Normal kindle links pull the author name as `lastName, firstName` whereas
	* audiobook links are pulled as `firstName, lastName`.
	*/
		if (!isAudioBook && this.book.author.split(',').length === 2) {
			const authorSplitName = this.book.author.split(',');
			this.book.author = `${authorSplitName[1]} ${authorSplitName[0]}`;
		}
		this.form.controls.author.patchValue(this.book.author);
		this._patch('author', this.book.author);
		this.book.title = bookAttributes.Title.DisplayValue;
		if (this.savedDetails?.book?.title && this.savedDetails?.book?.ASIN === this.book.ASIN) {
			this._patch('title', this.savedDetails.book.title);
		} else {
			this._patch('title', this.book.title);
		}

		if (bookImage) {
			this.book.amazonImageUrl = bookImage;
		}

		const { otherOptionsTitles, otherOptionsLinks, description, price } = scrapeResult;
		if (this.site.abbreviatedName !== 'NIB') {
			_.forEach(otherOptionsTitles, (option, i) => {
				if (option.match(/Paperback/)) {
					otherOptionsLinks[i] = '';
				} else if (option.match(/Hardcover/)) {
					otherOptionsLinks[i] = '';
				}
			});
		}

		this.bs.parseAmazonWhereBookAvailable(otherOptionsTitles, otherOptionsLinks, this.book);
		this.book.amazonBookDescription = description ? Book.parseAmazonBookDescription(description.replace(/\n+/g, ' ')) : '';
		Object.keys(this.featuredBook.whereAvailable).forEach(
			(item) => {
				const value = this.featuredBook.whereAvailable[item];
				if (value) {
					this._patch(item, true);
					this._patch(`${item}Link`, value);
				}
			},
		);
		this.featuredBook.kindlePrice = Book.parseAmazonKindlePrice(price, scrapeResult);
		this.featuredBook._book = this.book;

		// Trigger reinitialization of the book.
		this.book = { ...this.book };
	}

	selectBookLinkType(type: string) {
		this.bookType = type;
		this.showLinkOptions = false;
		this.book = new Book();
		this.featuredBook._book = new Book();

		if (type === 'bookShelf') {
			this.showBookShelf = true;
		}

		this._patch('amazonLink', '');
		this._patch('author', '');
		this._patch('title', '');

		if (type === 'TBD') {
			this.setTBDURL();
		}
	}

	setWhereAvailable(whereAvailable) {
		Object.keys(whereAvailable).forEach(
			(item) => {
				const value = whereAvailable[item];
				if (value) {
					this._patch(item, true);
					this._patch(`${item}Link`, value);
					this._activateSwitch(item);
				}
			},
		);
	}

	addSeries() {
		const id = `series_${this.seriesIncrement}`;
		this.seriesLinks.push({
			id,
			value: '',
			price: 0,
		});
		this.seriesIncrement += 1;
	}

	removeSeries(i) {
		this.seriesLinks.splice(i, 1);
	}

	handleKeyDownUndeterminedLink(event) {
		const { value } = event.target;

		if (event?.key?.toLowerCase() !== 'enter' || event.keyCode !== 13) {
			return;
		}

		this.validateNonAmazonUrl(value);

		if (this.linkError) {
			return;
		}

		if (!value.includes('amazon.com')) {
			return;
		}

		event.preventDefault();
		this.form.controls.amazonLink.patchValue(value);
		this.scrapeLink();
	}

	closeLearnMoreNonAmazonLink() {
		this.modalService.close('learn-more-non-amazon-link');
	}

	openLearnMoreNonAmazonLink() {
		this.modalService.open('learn-more-non-amazon-link');
	}

	validateNonAmazonUrl(value) {
		const urlRegexTest = /.+\..+/i.test(value);

		if (urlRegexTest === false) {
			this.linkError = true;
			return;
		}

		this.linkError = false;
	}

	handleScrapeUndeterminedLink(link = null) {
		const { value } = this.form.controls.link;

		const _v = value || link;

		if (!_v) {
			return;
		}

		this.validateNonAmazonUrl(_v);

		if (this.linkError) {
			return;
		}

		if (_v.includes('amazon.com')) {
			this.form.controls.amazonLink.patchValue(_v);
			this.scrapeLink();
			return;
		}

		this._patch('amazonLink', 'https://www.amazon.com/dp/null');
	}

	setTBDURL() {
		this.linkError = false;
		this._patch('amazonLink', 'https://www.amazon.com/dp/null');
		this._patch('author', 'TBD');
		this._patch('title', 'TBD');

		Object.assign(this.book, {
			amazonLink: 'https://www.amazon.com/dp/null',
			author: 'TBD',
			title: 'TBD',
		});
	}

	handleKeyDown(event) {
		if (event.key === 'enter' || event.keyCode === 13) {
			event.preventDefault();
			this.scrapeLink();
		}
	}

	dismissAmazonError() {
		this.amazonError = false;
		this.errorTitle = null;
	}

	dismissLinkError() {
		this.linkError = false;
	}

	scrollToField(id: string) {
		const el = document.getElementById(id);
		if (el) {
			el.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
				inline: 'nearest',
			});
		}

		this.validation.display = false;
	}

	onSubmit(event) {
		if (!this.form.value.terms) {
			return;
		}

		const validCover = this.validateCover();

		if (!validCover) {
			return;
		}

		event?.preventDefault();

		this.as.trackSubmitBook(this.siteSlug);

		// Clean URLs to remove leading or trailing spaces
		const isAudioThicket = (this.siteSlug === this.ss.slugs.AUDIO_THICKET_SLUG);
		const links: IWhereAvailable = {};

		this.retailers.forEach(({ name: retailer }) => {
			const linkId = `${retailer}Link`;
			links[linkId] = this.form.controls[linkId].value?.replace(/^\s+|\s+$/g, '');
		});

		if (this.featuredBookId) {
			this.featuredBook._id = this.featuredBookId;
		}

		if (Object.keys(this.featuredBook._book).length) {
			this.book = this.featuredBook._book;
		}

		if (this.inProgress) {
			return;
		}

		if (!this.displayPromoPriceField()) {
			this.form.removeControl('bookPriceDuringPromo');
		}

		const errors = this.ics.checkFormValid(this.form, this.formComponents);
		if (errors.hasErrors) {
			this._handleValidationErrors(errors);
			return;
		}

		let missingAmzURL = false;
		if (isAudioThicket) {
			if (!links.amazonLink) {
				missingAmzURL = true;
			} else {
				const rawUrl = links.amazonLink.split('?');
				const url = rawUrl[0];
				if (
					url.toLowerCase().indexOf('amazon.com/') === -1
						&& url.toLowerCase().indexOf('/dp/') === -1
				) {
					missingAmzURL = true;
				}
				const ASIN = Book.parseAmazonId(url);
				if (ASIN === 'null' || ASIN === null) {
					missingAmzURL = true;
				}
			}
		}

		this.form.value.bookPriceDuringPromo = this.utilitiesService.formatStringToNumber(this.form.value.bookPriceDuringPromo);

		this.inProgress = true;
		Object.keys(this.book).forEach(
			(item) => {
				// eslint-disable-next-line no-prototype-builtins
				if (links.hasOwnProperty(item)) this.book[item] = links[item];
				else if (this.form.value[item]) this.book[item] = this.form.value[item];
			},
		);
		Object.keys(this.featuredBook).forEach(
			(item) => {
				if (this.form.value[item]) {
					this.featuredBook[item] = this.form.value[item];
				}
			},
		);

		if (this.siteSlug === this.ss.slugs.FREEBOOKSY_SLUG) {
			this.featuredBook.bookPriceDuringPromo = 0;
		}
		this.featuredBook.subscribeToList = this.form.value.subscribeToList;
		let hasMinimumLinks = false;
		this.retailers.forEach(
			({ name: retailer }) => {
				const linkId = `${retailer}Link`;
				if (this.form.value[retailer] && links[linkId]) {
					this.book.whereAvailable[retailer] = links[linkId];
					hasMinimumLinks = true;

					if (this.primaryLink === retailer) {
						this.featuredBook.primaryLink = this.form.value[linkId];
					}
				}
			},
		);

		if (isAudioThicket && missingAmzURL && !hasMinimumLinks) {
			// this.errorTitle = ['Retailer Link Needed'];
			// this.errorText = ['A link to at least one retailer for your promotion is required.'];

			this.errorOptions.title = 'Retailer Link Needed';
			this.errorOptions.text = ['A link to at least one retailer for your promotion is required.'];
			this.modalService.open('fs4-error-modal');
			this.inProgress = false;
			return;
		}
		Object.keys(this.featuredBook.pricing).forEach(
			(item) => {
				if (this.form.value[item]) {
					this.featuredBook.pricing[item].discountPrice = this.form.value[`${item}Price`];
				}
			},
		);
		this.featuredBook.whereAvailable = this.book.whereAvailable;
		this.featuredBook.purchaserInfo.email = this.form.value.email.toLowerCase();
		this.featuredBook.purchaserInfo.firstName = this.form.value.firstName;
		this.featuredBook.purchaserInfo.lastName = this.form.value.lastName;
		this.featuredBook.purchaserInfo.memberRole = this.us.getMemberRoleId();
		this.featuredBook.paymentAmount = this.featuredBook.paymentAmount || this.siteAdvert.price;

		this.book.author = this.form.value.author;
		this.book.title = this.form.value.title;

		if (!this.featuredBook.purchaserInfo.memberRole) {
			delete this.featuredBook.purchaserInfo.memberRole;
		}
		this.featuredBook.purchaserInfo.loggedIn = this.us.loggedIn;

		this.featuredBook.purchaseSource = this.purchaseSource || 'Standard';

		this.featuredBook._book.numReviews = this.form.value.numReviews;
		this.featuredBook._book.reviewRating = this.form.value.reviewRating;
		this.book.link = this.form.value.link;
		const { pubDate } = this;
		if (this.isFbAdCampaign) {
			const endPubDate = parseInt(moment(pubDate, 'YYYYMMDD').add(4, 'days').format('YYYYMMDD'), 10);

			this.featuredBook.pubDate = parseInt(pubDate, 10);
			this.featuredBook.endPubDate = endPubDate;
		} else {
			this.featuredBook.pubDate = parseInt(pubDate, 10);
		}
		this.featuredBook._book.imageStoragePath = (this.bookImageUploadPath && this.bookImageUploadPath !== '')
			? this.bookImageUploadPath : null;
		if (this.user?._id) {
			this.featuredBook._owner = this.user._id;
		}
		this.featuredBook.emailOrder = this.siteAdvert.emailOrder;
		const ASIN = Book.parseAmazonId(this.book.amazonLink);
		this.book.amazonLink = `https://www.amazon.com/dp/${ASIN}`;
		if (this.purchaseProcessService.referrer) {
			this.featuredBook.referrer = this.purchaseProcessService.referrer;
		}
		if (this.featuredBook._book.isSeries) {
			this.featuredBook.seriesLinks = [];
			this.seriesLinks.forEach(
				(item) => {
					if ((<HTMLInputElement>document.getElementById(item.id)).value !== '') {
						this.featuredBook.seriesLinks.push({
							amazon: (<HTMLInputElement>document.getElementById(item.id)).value,
							ASIN: Book.parseAmazonId((<HTMLInputElement>document.getElementById(item.id)).value),
							price: Number((<HTMLInputElement>document.getElementById(`price_${item.id}`)).value),
							_book: null,
						});
					}
				},
			);
		}
		this.featuredBook.earlyAccess = this._isEarlyAccess();
		// Sync the featured book fields with any form changes
		this.featuredBook._book = this.book;
		this.featuredBook.description = this.featuredBook._book.description?.replace(/\n+/g, ' ');
		this.upsertFeaturedBook();
	}

	_isEarlyAccess() {
		const pubDate = moment(this.pubDate, 'YYYYMMDD');
		const inventoryEndDate = moment(this.site.inventoryAvailableTo, 'YYYYMMDD');

		return inventoryEndDate.diff(pubDate, 'days') <= this.EARLY_ACCESS_THRESHOLD;
	}

	_handleValidationErrors(errors) {
		if (this.showNewValidation && errors.requiredFields.length) {
			this.validation.fields = errors.requiredFields;
			this.validation.display = true;
		} else {
			// this.errorText = errors.errorText;
			this.errorOptions.text = errors.errorText;
			this.modalService.open('fs4-error-modal');
		}
	}

	upsertFeaturedBook() {
		if (this.featuredBook._id && this.purchaseProcessService.isEdit) {
			return this.fbs.updateFeaturedBook(this.featuredBook).subscribe(this.handleBookUpsertResult.bind(this));
		}
		delete this.featuredBook._id;
		return this.fbs.createFeaturedBook(this.featuredBook).subscribe(this.handleBookUpsertResult.bind(this));
	}

	handleBookUpsertResult(result): void {
		if (result?.featuredBook) {
			this.purchaseProcessService.feature = result.featuredBook;
		}

		this.inProgress = false;
		const authorPath = this.authorLink ? `/author-dashboard/${this.authorLink}` : '';
		const path = `${authorPath}/features/${this.siteSlug}/${this.siteAdvertSlug}/checkout/${result.featuredBook._id}`;
		this.router.navigate([path], { relativeTo: this.route });
	}

	handlePrimaryLinkUpdate(event) {
		this.primaryLink = event.target.value;
	}

	primaryLinkHoverIn() {
		this.primaryLinkHover = true;
	}

	primaryLinkHoverOut() {
		this.primaryLinkHover = false;
	}

	displayPromoPriceField() {
		if (this.siteAdvert.hideBookPriceDuringPromotion) {
			return false;
		}
		return this.siteSlug !== this.ss.slugs.FREEBOOKSY_SLUG
		&& (this.siteAdvert.priceThreshold > 0 || this.siteAdvert.priceRange.min > 0 || this.siteAdvert.priceRange.max > 0);
	}

	isFreeMember() {
		return this.us.isFreeMember();
	}

	validateCover() {
		const hasCover = !!this.featuredBook._book.amazonImageUrl || !!this.book.amazonImageUrl;
		const isTBD = this.form.controls.amazonLink.value === 'https://www.amazon.com/dp/null';
		const isNonTraditionalLink = !!this.form.controls.link.value;
		const isTBDNonTraditionalLink = this.form.controls.link.value === 'https://www.amazon.com/dp/null';

		// Has a cover
		if (hasCover) {
			return true;
		}
		// Is TBD and is a traditional link
		if (isTBD && !isNonTraditionalLink) {
			return true;
		}

		// Is TBD and a non-traditional link
		if (isNonTraditionalLink && isTBDNonTraditionalLink) {
			return true;
		}

		this.validation.fields = [{
			name: 'Book Cover',
			id: 'preview-cover',
		}];
		this.validation.display = true;

		if (this.bookType === 'TBD') {
			return true;
		}

		return false;
	}

	_activateSwitch(target) {
		setTimeout(() => {
			const input = document.querySelector(`#${target}`);
			if (!input) return;
			const btn = input.parentElement.querySelector('button');
			btn.classList.remove('toggle-btn-off');
			btn.classList.add('toggle-btn-on');

			btn.setAttribute('aria-expanded', 'true');
		}, 1000);
	}

	resetScroll() {
		window.scrollTo(0, 0);
	}

	setAdditionalAttributes(details) {
		this.form.get('terms').setValue(details.terms);
		this.form.get('title').setValue(this.book.title);
		this.form.get('author').setValue(this.book.author);
		this.form.get('description').setValue(this.book.description);
		this.form.get('amazonLink').setValue(this.book.amazonLink);

		const {
			purchaserInfoFirstName: firstName,
			purchaserInfoLastName: lastName,
			purchaserInfoEmail: email,
			promo_price: bookPriceDuringPromo,
		} = details;

		this._patch('firstName', firstName);
		this._patch('lastName', lastName);
		this._patch('email', email);
		this._patch('bookPriceDuringPromo', bookPriceDuringPromo);
		this._patch('kindleUnlimited', details.kindleUnlimited);
		this._patch('kindleCountdownDeal', details.kindleCountdownDeal);
		this._patch('subscribeToList', details.subscribeToList);
	}
}
